class DeliveryCutoffTimer extends HTMLElement {
  connectedCallback() {
      const container = this;

      const workingDays = container.dataset.workingDays
         .split(',')
         .map(s => s.trim())
         .filter(Boolean)
         .map(Number);

      const cutoffHour = parseInt(container.dataset.cutoffHour, 10);
      const cutoffMinute = parseInt(container.dataset.cutoffMinute, 10);
      const shippingDays = parseInt(container.dataset.shippingDays, 10);
      const shippingDayMax = parseInt(container.dataset.shippingDayMax, 10);
      const template = container.dataset.messageTemplate;
      const storeTimeZone = container.dataset.storeTimezone || 'Europe/London';
      const staticMode = container.dataset.staticMode === 'true';

      const outputEl = container.querySelector('[data-cutoff-output]');
      if (!outputEl) return;

      function getStoreNow() {
          const now = new Date();
          const formatter = new Intl.DateTimeFormat('en-GB', {
              timeZone: storeTimeZone,
              hour12: false,
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit'
          });

          const parts = formatter.formatToParts(now).reduce((acc, part) => {
              if (part.type !== 'literal') acc[part.type] = part.value;
              return acc;
          }, {});

          return new Date(`${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}:${parts.second}`);
      }

      function isWorkingDay(date) {
          return workingDays.includes(date.getDay());
      }

      function getNextWorkingDay(date) {
          const d = new Date(date);
          do {
              d.setDate(d.getDate() + 1);
          } while (!isWorkingDay(d));
          return d;
      }

      function addWorkingDays(date, days) {
          let d = new Date(date);
          let added = 0;
          while (added < days) {
              d.setDate(d.getDate() + 1);
              if (isWorkingDay(d)) {
                  added++;
              }
          }
          return d;
      }

      function getOrdinalSuffix(day) {
          if (day > 3 && day < 21) return 'th'; // catch 11–13
          switch (day % 10) {
              case 1:  return 'st';
              case 2:  return 'nd';
              case 3:  return 'rd';
              default: return 'th';
          }
      }

      function updateTimer() {
          const now = getStoreNow();
          let dispatchDate = new Date(now);
          let cutoffTimeToday = new Date(dispatchDate);
          cutoffTimeToday.setHours(cutoffHour, cutoffMinute, 0, 0);

          const isTodayWorkingDay = isWorkingDay(now);
          if (!isTodayWorkingDay || now > cutoffTimeToday) {
              dispatchDate = getNextWorkingDay(dispatchDate);
          }

          const deliveryDate = addWorkingDays(dispatchDate, shippingDays);

          let finalDispatchDate = new Date(deliveryDate);
          let shippingCountdown = shippingDays;
          while (shippingCountdown > 0) {
              finalDispatchDate.setDate(finalDispatchDate.getDate() - 1);
              if (isWorkingDay(finalDispatchDate)) {
                  shippingCountdown--;
              }
          }

          const cutoffDeadline = new Date(finalDispatchDate);
          cutoffDeadline.setHours(cutoffHour, cutoffMinute, 0, 0);

          const timeDiffMs = cutoffDeadline - now;
          const secondsLeft = Math.max(0, Math.floor(timeDiffMs / 1000));
          const hours = Math.floor(secondsLeft / 3600);
          const minutes = Math.floor((secondsLeft % 3600) / 60);
          const seconds = secondsLeft % 60;

          // Build time string, only show hours if > 0
          let timeComponents = [];
          if (hours > 0) {
              timeComponents.push(`${hours} hour${hours !== 1 ? 's' : ''}`);
          }
          timeComponents.push(`${minutes} minute${minutes !== 1 ? 's' : ''}`);
          
          // In static mode, don't show seconds
          if (!staticMode) {
              timeComponents.push(`${seconds} second${seconds !== 1 ? 's' : ''}`);
          }

          const timeLeftFormatted = `<span class="cutoff-timer__time-left">${timeComponents.join(', ')}</span>`;

          // Helper to check if a date is tomorrow
          function isTomorrow(date) {
              const tomorrow = new Date(now);
              tomorrow.setDate(tomorrow.getDate() + 1);
              return date.getDate() === tomorrow.getDate() &&
                     date.getMonth() === tomorrow.getMonth() &&
                     date.getFullYear() === tomorrow.getFullYear();
          }

          // Format delivery date(s)
          let deliveryFormatted;
          if (shippingDayMax && shippingDayMax > shippingDays) {
              // Show date range - always show dates, not "Tomorrow"
              const maxDeliveryDate = addWorkingDays(dispatchDate, shippingDayMax);
              
              const minMonth = deliveryDate.toLocaleDateString('en-GB', { month: 'long' });
              const minDay = deliveryDate.getDate();
              const minDayOrdinal = minDay + getOrdinalSuffix(minDay);
              const maxMonth = maxDeliveryDate.toLocaleDateString('en-GB', { month: 'long' });
              const maxDay = maxDeliveryDate.getDate();
              const maxDayOrdinal = maxDay + getOrdinalSuffix(maxDay);
              
              deliveryFormatted = `<span class="cutoff-timer__delivery-date">${minMonth} ${minDayOrdinal} - ${maxMonth} ${maxDayOrdinal}</span>`;
          } else {
              // Show single date - use "Tomorrow" if applicable
              if (isTomorrow(deliveryDate)) {
                  deliveryFormatted = `<span class="cutoff-timer__delivery-date">Tomorrow</span>`;
              } else {
                  const month = deliveryDate.toLocaleDateString('en-GB', { month: 'long' });
                  const day = deliveryDate.getDate();
                  const dayOrdinal = day + getOrdinalSuffix(day);
                  deliveryFormatted = `<span class="cutoff-timer__delivery-date">${month} ${dayOrdinal}</span>`;
              }
          }

          const finalMessage = template
             .replace('[[time_left]]', timeLeftFormatted)
             .replace('[[delivery_day]]', deliveryFormatted);

          outputEl.innerHTML = finalMessage;
      }

      // Initial update
      updateTimer();

      // In static mode, only run once. Otherwise update every second
      if (!staticMode) {
          this.intervalId = setInterval(updateTimer, 1000);
      }
  }

  disconnectedCallback() {
      // Clean up interval when component is removed
      if (this.intervalId) {
          clearInterval(this.intervalId);
      }
  }
}

customElements.define('delivery-cutoff-timer', DeliveryCutoffTimer);